import React, { Fragment, useEffect, useState } from 'react';

import { useEventTracking } from '@hooks';
import { Category } from '@interfaces/ti50';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { parameterize } from '@utils/formatting';

interface CategoryHeaderProps {
  activeCategories: Category[];
}

const CategoryHeader = ({ activeCategories }: CategoryHeaderProps) => {
  const [activeSection, setActiveSection] = useState<string>('');
  const { trackEvent } = useEventTracking({ location: TrackingLocation.TI502025CategoryHeader });

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-40% 0px -60% 0px',
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          const category = sectionId.replace('outer-section-', '');
          setActiveSection(category);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    activeCategories.forEach(({ category }) => {
      const sectionElement = document.getElementById(`outer-section-${category}`);
      if (sectionElement) {
        observer.observe(sectionElement);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [activeCategories]);

  return (
    <div className="fixed left-0 right-0 top-64 z-20 hidden bg-white p-16 md:block">
      <div className="mx-auto flex max-w-container-lg items-center justify-between">
        {activeCategories.map(({ category }, idx) => {
          const isActive = activeSection === category;
          return (
            <Fragment key={category}>
              <a
                className={`font-suisse-intl text-icon-lg hover:text-black ${
                  isActive ? 'text-black' : 'text-grayscale-gray'
                }`}
                href={`#${parameterize(category)}`}
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.AnchorLink, clickText: category }))
                }
              >
                {category}
              </a>
              {idx !== activeCategories.length - 1 && <div className="h-20 w-px bg-black" />}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryHeader;
